/**
 * `/admin/referrals` — operator/marketing surface for the campaign-driven
 * referral + affiliate engine (OCP-382). Pairs with
 * `/v1/admin/referrals/*`. Create campaigns, mint codes, watch attributions.
 *
 * Reward config is stored as a flexible JSON object, but marketing never
 * sees or types JSON (OCP-495): kind-aware form fields emit exactly the
 * shape the engines read - referral -> `{ referrerCreditCents,
 * referredCreditCents }` (resolveRewardAmounts, OCP-383), affiliate ->
 * `{ commissionModel: 'pct_first_payment', pctBps }` or
 * `{ commissionModel: 'flat', flatCents }` (resolveRate, OCP-492).
 * Campaigns are editable after creation - without that the commission rate
 * would be frozen at create time and the OCP-492 "set a rate before handing
 * out codes" step would be impossible.
 */
import { Fragment, useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

interface Campaign {
  id: string;
  name: string;
  kind: 'referral' | 'affiliate';
  trigger: 'paid_conversion' | 'signup';
  /** Nullable on the wire - campaigns can exist without a reward config
   *  (pre-OCP-495 rows, API create without the field). Renderers must not
   *  assume an object (OCP-501 prod crash). */
  reward_config: Record<string, unknown> | null;
  status: 'active' | 'paused' | 'ended';
  starts_at: string | null;
  ends_at: string | null;
  created_at: string;
}
interface Code {
  id: string;
  code: string;
  owner_type: 'customer' | 'affiliate' | 'campaign';
  owner_id: string | null;
  created_at: string;
}
interface Attribution {
  id: string;
  customer_id: string;
  campaign_id: string;
  ref_code: string;
  referrer_type: string;
  referrer_id: string | null;
  status: 'pending' | 'converted' | 'rejected';
  converted_at: string | null;
  created_at: string;
}

export function AdminReferralsPage() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [attributions, setAttributions] = useState<Attribution[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [codes, setCodes] = useState<Record<string, Code[]>>({});

  // create-campaign form
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'referral' | 'affiliate'>('referral');
  const [trigger, setTrigger] = useState<'paid_conversion' | 'signup'>('paid_conversion');
  const [reward, setReward] = useState<RewardForm>(EMPTY_REWARD);
  // OCP-495: the campaign being edited (null = none).
  const [editing, setEditing] = useState<Campaign | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [c, a] = await Promise.all([
        api<{ campaigns: Campaign[] }>('/v1/admin/referrals/campaigns'),
        api<{ attributions: Attribution[] }>('/v1/admin/referrals/attributions'),
      ]);
      setCampaigns(c.campaigns);
      setAttributions(a.attributions);
    } catch (e) {
      setError(e instanceof ApiError ? `Load failed (HTTP ${e.status})` : 'Load failed');
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  async function createCampaign() {
    setError(null);
    const built = buildRewardConfig(kind, reward);
    if ('error' in built) {
      setError(built.error);
      return;
    }
    try {
      await api('/v1/admin/referrals/campaigns', {
        method: 'POST',
        body: { name: name.trim(), kind, trigger, reward_config: built.config },
      });
      setName('');
      setReward(EMPTY_REWARD);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? `Create failed (HTTP ${e.status})` : 'Create failed');
    }
  }

  async function toggleStatus(c: Campaign) {
    const next = c.status === 'active' ? 'paused' : 'active';
    try {
      await api(`/v1/admin/referrals/campaigns/${c.id}`, { method: 'PATCH', body: { status: next } });
      await load();
    } catch {
      setError('Status change failed');
    }
  }

  async function loadCodes(id: string) {
    if (expanded === id) {
      setExpanded(null);
      return;
    }
    setExpanded(id);
    try {
      const r = await api<{ codes: Code[] }>(`/v1/admin/referrals/campaigns/${id}/codes`);
      setCodes((prev) => ({ ...prev, [id]: r.codes }));
    } catch {
      setError('Could not load codes');
    }
  }

  async function mintCode(id: string, code: string) {
    if (!code.trim()) return;
    try {
      await api(`/v1/admin/referrals/campaigns/${id}/codes`, {
        method: 'POST',
        body: { code: code.trim(), owner_type: 'campaign' },
      });
      const r = await api<{ codes: Code[] }>(`/v1/admin/referrals/campaigns/${id}/codes`);
      setCodes((prev) => ({ ...prev, [id]: r.codes }));
    } catch (e) {
      setError(e instanceof ApiError && e.status === 409 ? 'Code already taken' : 'Mint failed');
    }
  }

  return (
    <section className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Referral &amp; affiliate campaigns</h1>
        <p className="text-sm text-gray-500">Marketing configures campaigns; the engine applies the trigger + reward.</p>
      </div>
      {error && (
        <div role="alert" className="rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Create campaign */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 space-y-2">
        <h2 className="text-sm font-semibold">New campaign</h2>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 text-sm" />
          <select value={kind} onChange={(e) => setKind(e.target.value as 'referral' | 'affiliate')} className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 text-sm">
            <option value="referral">referral (give-get)</option>
            <option value="affiliate">affiliate (revshare)</option>
          </select>
          <select value={trigger} onChange={(e) => setTrigger(e.target.value as 'paid_conversion' | 'signup')} className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 text-sm">
            <option value="paid_conversion">trigger: paid conversion</option>
            <option value="signup">trigger: signup</option>
          </select>
          <button type="button" onClick={() => void createCampaign()} disabled={!name.trim()} className="rounded bg-brand-500 px-3 py-1 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50">Create</button>
        </div>
        <RewardConfigFields kind={kind} value={reward} onChange={setReward} />
      </div>

      {/* Campaign list */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase text-gray-500">
            <tr><th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Kind</th><th className="px-3 py-2 text-left">Trigger</th><th className="px-3 py-2 text-left">Reward</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-right">Codes / actions</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
            {campaigns.length === 0 && (<tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-gray-500">No campaigns yet.</td></tr>)}
            {campaigns.map((c) => (
              <Fragment key={c.id}>
                <tr>
                  <td className="px-3 py-2">{c.name}</td>
                  <td className="px-3 py-2">{c.kind}</td>
                  <td className="px-3 py-2 text-xs">{c.trigger}</td>
                  {/* OCP-495: readable summary, never raw JSON. An unresolvable
                      config is called out - the engine would skip sales on it. */}
                  <td className="px-3 py-2 text-xs" data-testid={`reward-summary-${c.id}`}>
                    {describeReward(c.kind, c.reward_config)}
                  </td>
                  <td className="px-3 py-2"><span className={`rounded px-1.5 py-0.5 text-xs ${c.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>{c.status}</span></td>
                  <td className="px-3 py-2 text-right">
                    <button type="button" onClick={() => setEditing(c)} className="text-xs text-brand-500 hover:underline">Edit</button>
                    <button type="button" onClick={() => void loadCodes(c.id)} className="ml-3 text-xs text-brand-500 hover:underline">{expanded === c.id ? 'Hide codes' : 'Codes'}</button>
                    <button type="button" onClick={() => void toggleStatus(c)} className="ml-3 text-xs text-brand-500 hover:underline">{c.status === 'active' ? 'Pause' : 'Activate'}</button>
                  </td>
                </tr>
                {expanded === c.id && (
                  <tr className="bg-gray-50 dark:bg-gray-950/40">
                    <td colSpan={6} className="px-3 py-2">
                      <CodesPanel codes={codes[c.id] ?? []} onMint={(code) => void mintCode(c.id, code)} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Attributions */}
      <div>
        <h2 className="mb-2 text-sm font-semibold">Recent attributions</h2>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase text-gray-500">
              <tr><th className="px-3 py-2 text-left">Code</th><th className="px-3 py-2 text-left">Referrer</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">When</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {attributions.length === 0 && (<tr><td colSpan={4} className="px-3 py-6 text-center text-sm text-gray-500">No attributions yet.</td></tr>)}
              {attributions.map((a) => (
                <tr key={a.id}>
                  <td className="px-3 py-2 font-mono text-xs">{a.ref_code}</td>
                  <td className="px-3 py-2 text-xs">{a.referrer_type}{a.referrer_id ? ` · ${a.referrer_id.slice(0, 8)}` : ''}</td>
                  <td className="px-3 py-2"><span className={`rounded px-1.5 py-0.5 text-xs ${a.status === 'converted' ? 'bg-green-100 text-green-800' : a.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-200 text-gray-600'}`}>{a.status}</span></td>
                  <td className="px-3 py-2 text-xs text-gray-500">{fmtDateTime(a.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <CampaignEditModal
          campaign={editing}
          onCancel={() => setEditing(null)}
          onDone={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </section>
  );
}

function CodesPanel({ codes, onMint }: { codes: Code[]; onMint: (code: string) => void }) {
  const [val, setVal] = useState('');
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {codes.length === 0 ? <span className="text-xs text-gray-500">No codes — mint one to share as <code>?ref=&lt;code&gt;</code>.</span> : codes.map((c) => (
          <span key={c.id} className="rounded bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 font-mono text-xs">{c.code} <span className="text-gray-500">({c.owner_type})</span></span>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={val} onChange={(e) => setVal(e.target.value)} placeholder="NEWCODE" className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 font-mono text-xs" />
        <button type="button" onClick={() => { onMint(val); setVal(''); }} disabled={!val.trim()} className="rounded border border-gray-300 dark:border-gray-700 px-2.5 py-1 text-xs hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50">Mint code</button>
      </div>
    </div>
  );
}

// ---- OCP-495: typed reward config (no JSON in the UI) ------------------

/** Marketing-facing form state. Amounts are EUR strings; % is whole-percent. */
interface RewardForm {
  referrerEur: string;
  referredEur: string;
  model: 'pct_first_payment' | 'flat';
  pct: string;
  flatEur: string;
}

const EMPTY_REWARD: RewardForm = {
  referrerEur: '',
  referredEur: '',
  model: 'pct_first_payment',
  pct: '',
  flatEur: '',
};

function eurToCents(v: string): number | null {
  const n = Number(v.trim());
  if (v.trim() === '' || !Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

function readNum(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/**
 * Build EXACTLY the config shape the engines resolve:
 *   referral -> { referrerCreditCents, referredCreditCents }  (OCP-383 resolveRewardAmounts)
 *   affiliate -> { commissionModel:'pct_first_payment', pctBps } | { commissionModel:'flat', flatCents }
 *                (OCP-492 resolveRate / campaignBps - pctBps is the exact-integer key it prefers)
 * Anything the engine could not resolve is rejected here, so marketing can
 * never save a campaign that silently skips sales as `no_rate`.
 */
export function buildRewardConfig(
  kind: 'referral' | 'affiliate',
  form: RewardForm,
): { config: Record<string, unknown> } | { error: string } {
  if (kind === 'referral') {
    const referrer = form.referrerEur.trim() === '' ? 0 : eurToCents(form.referrerEur);
    const referred = form.referredEur.trim() === '' ? 0 : eurToCents(form.referredEur);
    if (referrer === null || referred === null) {
      return { error: 'Credit amounts must be positive numbers.' };
    }
    if (referrer <= 0 && referred <= 0) {
      return { error: 'Set at least one credit amount above 0 - otherwise no reward is ever issued.' };
    }
    return { config: { referrerCreditCents: referrer, referredCreditCents: referred } };
  }
  if (form.model === 'flat') {
    const cents = eurToCents(form.flatEur);
    if (cents === null || cents <= 0) return { error: 'Flat commission must be above 0.' };
    return { config: { commissionModel: 'flat', flatCents: cents } };
  }
  const pct = Number(form.pct.trim());
  if (form.pct.trim() === '' || !Number.isFinite(pct) || pct <= 0 || pct > 100) {
    return { error: 'Commission % must be between 0 and 100.' };
  }
  return { config: { commissionModel: 'pct_first_payment', pctBps: Math.round(pct * 100) } };
}

/**
 * Pre-fill the form from a stored config (edit). Tolerates legacy `pct` and
 * a missing config entirely (null/undefined -> empty form, OCP-501).
 */
export function parseRewardConfig(
  kind: 'referral' | 'affiliate',
  rawConfig: Record<string, unknown> | null | undefined,
): RewardForm {
  const config = rawConfig ?? {};
  if (kind === 'referral') {
    const referrer = readNum(config.referrerCreditCents);
    const referred = readNum(config.referredCreditCents);
    return {
      ...EMPTY_REWARD,
      referrerEur: referrer ? (referrer / 100).toFixed(2) : '',
      referredEur: referred ? (referred / 100).toFixed(2) : '',
    };
  }
  const model = config.commissionModel === 'flat' ? 'flat' : 'pct_first_payment';
  const bps = readNum(config.pctBps);
  const legacyPct = readNum(config.pct);
  const flat = readNum(config.flatCents);
  return {
    ...EMPTY_REWARD,
    model,
    pct: bps ? String(bps / 100) : legacyPct ? String(legacyPct) : '',
    flatEur: flat ? (flat / 100).toFixed(2) : '',
  };
}

/**
 * Human-readable summary for the list - never raw JSON. A missing config
 * (null/undefined) reads as "not set", it must never throw (OCP-501).
 */
export function describeReward(
  kind: 'referral' | 'affiliate',
  rawConfig: Record<string, unknown> | null | undefined,
): string {
  const config = rawConfig ?? {};
  if (kind === 'referral') {
    const referrer = readNum(config.referrerCreditCents) ?? 0;
    const referred = readNum(config.referredCreditCents) ?? 0;
    if (referrer <= 0 && referred <= 0) return 'not set - no reward issued';
    return `${(referrer / 100).toFixed(2)} EUR referrer / ${(referred / 100).toFixed(2)} EUR referred`;
  }
  if (config.commissionModel === 'flat') {
    const flat = readNum(config.flatCents) ?? 0;
    return flat > 0 ? `${(flat / 100).toFixed(2)} EUR flat per deal` : 'not set - sales skipped';
  }
  const bps = readNum(config.pctBps);
  const legacyPct = readNum(config.pct);
  const pct = bps !== null ? bps / 100 : legacyPct;
  return pct && pct > 0 ? `${pct}% of first payment` : 'not set - sales skipped';
}

/** Kind-aware reward inputs. Shared by the create form + the edit modal. */
function RewardConfigFields({
  kind,
  value,
  onChange,
}: {
  kind: 'referral' | 'affiliate';
  value: RewardForm;
  onChange: (next: RewardForm) => void;
}) {
  const input =
    'mt-0.5 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 text-sm';
  if (kind === 'referral') {
    return (
      <div className="grid grid-cols-2 gap-3" data-testid="reward-fields-referral">
        <label className="block text-xs text-gray-500">
          Referrer credit (EUR)
          <input
            type="number" min="0" step="0.01" value={value.referrerEur}
            onChange={(e) => onChange({ ...value, referrerEur: e.target.value })}
            className={input} data-testid="reward-referrer-eur"
          />
        </label>
        <label className="block text-xs text-gray-500">
          Referred credit (EUR)
          <input
            type="number" min="0" step="0.01" value={value.referredEur}
            onChange={(e) => onChange({ ...value, referredEur: e.target.value })}
            className={input} data-testid="reward-referred-eur"
          />
        </label>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="reward-fields-affiliate">
      <label className="block text-xs text-gray-500">
        Commission model
        <select
          value={value.model}
          onChange={(e) => onChange({ ...value, model: e.target.value as RewardForm['model'] })}
          className={input} data-testid="reward-model"
        >
          <option value="pct_first_payment">Percent of first payment</option>
          <option value="flat">Flat per deal</option>
        </select>
      </label>
      {value.model === 'flat' ? (
        <label className="block text-xs text-gray-500">
          Flat commission (EUR)
          <input
            type="number" min="0" step="0.01" value={value.flatEur}
            onChange={(e) => onChange({ ...value, flatEur: e.target.value })}
            className={input} data-testid="reward-flat-eur"
          />
        </label>
      ) : (
        <label className="block text-xs text-gray-500">
          Commission %
          <input
            type="number" min="0" max="100" step="0.01" value={value.pct}
            onChange={(e) => onChange({ ...value, pct: e.target.value })}
            className={input} data-testid="reward-pct"
          />
        </label>
      )}
    </div>
  );
}

/**
 * Edit an existing campaign (OCP-495). Without this the reward config was
 * frozen at create time - an affiliate campaign's commission rate could
 * never be set or corrected afterwards.
 */
function CampaignEditModal({
  campaign,
  onCancel,
  onDone,
}: {
  campaign: Campaign;
  onCancel: () => void;
  onDone: () => Promise<void>;
}) {
  const [name, setName] = useState(campaign.name);
  const [trigger, setTrigger] = useState(campaign.trigger);
  const [reward, setReward] = useState<RewardForm>(() =>
    parseRewardConfig(campaign.kind, campaign.reward_config),
  );
  const [status, setStatus] = useState(campaign.status);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (busy) return;
    setError(null);
    const built = buildRewardConfig(campaign.kind, reward);
    if ('error' in built) {
      setError(built.error);
      return;
    }
    setBusy(true);
    try {
      await api(`/v1/admin/referrals/campaigns/${campaign.id}`, {
        method: 'PATCH',
        body: { name: name.trim(), trigger, reward_config: built.config, status },
      });
      await onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `Save failed (HTTP ${e.status})` : 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Edit campaign"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl"
      >
        <h2 className="mb-3 text-base font-semibold">Edit campaign</h2>
        <div className="space-y-3">
          <label className="block text-xs text-gray-500">
            Name
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-0.5 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 text-sm"
              data-testid="edit-name"
            />
          </label>
          <label className="block text-xs text-gray-500">
            Trigger
            <select
              value={trigger}
              onChange={(e) => setTrigger(e.target.value as Campaign['trigger'])}
              className="mt-0.5 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 text-sm"
            >
              <option value="paid_conversion">paid_conversion</option>
              <option value="signup">signup</option>
            </select>
          </label>
          <RewardConfigFields kind={campaign.kind} value={reward} onChange={setReward} />
          <label className="block text-xs text-gray-500">
            Status
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as Campaign['status'])}
              className="mt-0.5 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1 text-sm"
            >
              <option value="active">active</option>
              <option value="paused">paused</option>
              <option value="ended">ended</option>
            </select>
          </label>
        </div>
        {error && <div className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</div>}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button" onClick={onCancel}
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300"
          >
            Cancel
          </button>
          <button
            type="button" disabled={busy} onClick={() => void save()}
            className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
            data-testid="edit-save"
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
